import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Heading,
  Text,
  Badge,
  Select,
  Container,
  Link,
} from "@chakra-ui/react"
import { neumorph } from "./neumorph"
import { usePeriod, PERIOD_OPTIONS } from "./PeriodContext"

const NAV_SECTIONS = [
  { id: "overview", label: "Overview" },
  { id: "trends", label: "Trends" },
  { id: "performance", label: "Performance" },
  { id: "inventory", label: "Inventory" },
]

const InsightsTopNav = ({ supplier, retailer }) => {
  const { periodMonths, setPeriodMonths } = usePeriod()
  const [activeSection, setActiveSection] = useState("overview")

  // Update active section as user scrolls
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY + 200
      let current = "overview"
      for (const sec of NAV_SECTIONS) {
        const el = document.getElementById(sec.id)
        if (el && el.offsetTop <= scrollY) current = sec.id
      }
      setActiveSection(current)
    }
    window.addEventListener("scroll", handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const handleNavClick = (e, id) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      window.scrollTo({ top: el.offsetTop - 100, behavior: "smooth" })
      setActiveSection(id)
    }
  }

  return (
    <Box
      sx={{
        ...neumorph.stickyHeader,
        position: "sticky",
        top: 0,
        zIndex: 20,
        py: 3,
      }}
    >
      <Container maxW="98vw">
        <Flex justify="space-between" align="center" mb={3} wrap="wrap" gap={3}>
          {/* Title */}
          <Flex align="center" gap={2}>
            <Heading size="md" color={neumorph.textPrimary}>
              Insights
            </Heading>
            <Badge
              ml={1}
              fontSize="2xs"
              px={2}
              py={0.5}
              borderRadius="full"
              bg={neumorph.accentTeal}
              color="white"
            >
              ADMIN PREVIEW
            </Badge>
            {(supplier || retailer) && (
              <Text fontSize="sm" color={neumorph.textMuted} ml={3}>
                {supplier} {retailer && `· ${retailer}`}
              </Text>
            )}
          </Flex>

          {/* Period selector — neumorphic styled */}
          <Flex align="center" gap={2}>
            <Text fontSize="xs" color={neumorph.textMuted} fontWeight="600">
              PERIOD
            </Text>
            <Select
              size="sm"
              value={periodMonths}
              onChange={(e) => setPeriodMonths(e.target.value)}
              w="180px"
              sx={{
                ...neumorph.pressed,
                border: "none",
                fontSize: "sm",
                fontWeight: "600",
                color: neumorph.accentTealDark,
                _focus: { boxShadow: neumorph.pressed.boxShadow },
              }}
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>

        {/* Anchor nav */}
        <Flex gap={2} wrap="wrap">
          {NAV_SECTIONS.map((sec) => {
            const isActive = activeSection === sec.id
            return (
              <Link
                key={sec.id}
                href={`#${sec.id}`}
                onClick={(e) => handleNavClick(e, sec.id)}
                _hover={{ textDecoration: "none" }}
                sx={{
                  ...(isActive ? neumorph.pressed : neumorph.raisedSm),
                  px: 4,
                  py: 1.5,
                  fontSize: "xs",
                  fontWeight: "700",
                  letterSpacing: "0.05em",
                  textTransform: "uppercase",
                  color: isActive ? neumorph.accentTealDark : neumorph.textMuted,
                  borderBottom: isActive ? `2px solid ${neumorph.accentTeal}` : "2px solid transparent",
                  borderRadius: "12px",
                  cursor: "pointer",
                  display: "inline-block",
                }}
              >
                {sec.label}
              </Link>
            )
          })}
        </Flex>
      </Container>
    </Box>
  )
}

export default InsightsTopNav
